// Which networks the Overview panels are about (UX18).
//
// The Tool usage bars and the Network graph both used to render EVERYTHING the
// gateway returned. For a member that is nearly right by accident — the server
// scopes /networks by identity — but for an admin it is 652 nodes of other people's
// fleet on the page that is supposed to say "how are MY things doing", and the tool
// bars underneath it counted calls into networks the reader has never opened.
//
// So the two panels take a SCOPE. Everyone starts on `mine`: the networks you own or
// are a member of. An admin — the only role for whom the other answers exist — gets
// the rest as a control: everything the server returned, or a slice by visibility.
//
// Everything here is pure so the filtering is testable without a DOM, and so the
// same rule applies to both panels: a graph and a bar chart that disagree about which
// networks are in scope are worse than either alone.

import type { Network, NetworkMembership } from '../api/schemas';

export const NETWORK_SCOPES = ['mine', 'all', 'public', 'private', 'org'] as const;
export type NetworkScope = (typeof NETWORK_SCOPES)[number];

/** The default, for every role: the reader's own networks. */
export const DEFAULT_SCOPE: NetworkScope = 'mine';

export const SCOPE_LABEL: Record<NetworkScope, string> = {
  mine: 'Mine',
  all: 'All',
  public: 'Public',
  private: 'Private',
  org: 'Organization',
};

export const SCOPE_TITLE: Record<NetworkScope, string> = {
  mine: 'Networks you own or are a member of',
  all: 'Every network the gateway returned for you',
  public: 'Networks visible to anyone',
  private: 'Networks visible only to their members',
  org: 'Networks visible across the organization',
};

/** Is a network one of the caller's own — owned, or held by a membership? */
export function isOwnNetwork(
  n: Pick<Network, 'id' | 'owner_id'>,
  userId: string | undefined,
  memberships: Pick<NetworkMembership, 'network_id'>[],
): boolean {
  if (userId && n.owner_id === userId) return true;
  return memberships.some((m) => m.network_id === n.id);
}

/**
 * The networks a scope selects.
 *
 * `all` is the identity — it returns the list untouched, which is what the panels
 * did before this existed. The three visibility scopes are a plain field match; only
 * `mine` needs to know who is asking, and it answers from /auth/me's memberships plus
 * ownership, both of which the session already holds. No extra request.
 */
export function scopeNetworks(
  networks: Network[],
  scope: NetworkScope,
  userId: string | undefined,
  memberships: Pick<NetworkMembership, 'network_id'>[],
): Network[] {
  switch (scope) {
    case 'all':
      return networks;
    case 'mine':
      return networks.filter((n) => isOwnNetwork(n, userId, memberships));
    default:
      return networks.filter((n) => n.visibility === scope);
  }
}

/**
 * Keep a per-tool usage row only when its tool belongs to one of these networks.
 *
 * A canonical tool is `network__service__tool`, so the network is the first segment —
 * the same split the graph's tool labels use. A row whose name does not parse (an
 * older gateway, a builtin with no network) is DROPPED while a scope is active: the
 * panel claims to be about a set of networks, and a row that cannot say which network
 * it belongs to cannot be claimed to be in it.
 */
/**
 * Keep a task only when it belongs to one of these networks.
 *
 * The Tasks panel sits under the network graph and the queues, all three on one page that
 * announces which networks it is about — so it takes the same scope they do, by `network_id`
 * (a task carries the id outright; no name parsing needed, unlike the usage rows above).
 *
 * `null` means "no opinion" — the scope has nothing loaded to filter BY yet, and cutting to an
 * empty set would draw an empty table and call it an answer. A task with no `network_id` is
 * DROPPED while a scope is active, for the same reason a usage row that cannot name its network
 * is: the panel claims to be about a set of networks.
 *
 * For a member this is belt-and-braces — the gateway already returns only the tasks of networks
 * they belong to, and there is no control to move the scope off `mine`. For an admin it is the
 * whole point: the segmented control is the only thing standing between them and the org's
 * entire ledger on a page that asks how *their* things are doing.
 */
export function scopeTasks<T extends { network_id?: string | null }>(
  rows: T[],
  ids: Set<string> | null,
): T[] {
  if (!ids) return rows;
  return rows.filter((r) => !!r.network_id && ids.has(r.network_id));
}

// ---------------------------------------------------------------------------------------------
// OW1 — whose tasks a NON-ADMIN's Tasks panel lists: «Мои» / «Сети».
//
// The network scope above is the admin's control; a member has one answer to "which networks" and
// no control for it. But a member DOES have two answers to "which tasks", and the membership ledger
// only knew one of them: the networks they belong to. The calls they make through the org's
// platform network — /mcp grants every user implicit membership of it, without a membership row —
// never appeared, although each one's poll URL opens.
//
//   own      («Мои»)  — `GET /tasks?scope=own`: the caller's OWN calls (by requesting user), in
//                       whichever network of their org they ran, the platform network included.
//                       Nobody else's work, in any network.
//   networks («Сети») — `GET /tasks?scope=networks`: the member ledger — every member's tasks in the
//                       networks the caller belongs to (what the panel always showed a member).
//
// An admin's panel is unchanged: no task-scope control, no `scope` param (org-wide), narrowed only
// by the network scope above. The gateway rejects any other `scope` value, so the client cannot
// ask for more than these two even by accident.
// ---------------------------------------------------------------------------------------------
export const TASK_SCOPES = ['own', 'networks'] as const;
export type TaskScope = (typeof TASK_SCOPES)[number];

/** A member opens on their own calls — the question the panel is asked most. */
export const DEFAULT_TASK_SCOPE: TaskScope = 'own';

export const TASK_SCOPE_LABEL: Record<TaskScope, string> = {
  own: 'Мои',
  networks: 'Сети',
};

export const TASK_SCOPE_TITLE: Record<TaskScope, string> = {
  own: 'Запросы, сделанные вашими токенами — в любой сети организации, включая платформенную',
  networks: 'Все задачи в сетях, участником которых вы являетесь',
};

export function isTaskScope(v: unknown): v is TaskScope {
  return typeof v === 'string' && (TASK_SCOPES as readonly string[]).includes(v);
}

/**
 * The `scope` query param the Tasks panel sends. `undefined` for an admin (their request is
 * unchanged) and while the session is unknown (nothing to decide BY yet).
 */
export function taskListScope(
  role: { isAdmin: boolean; signedIn: boolean },
  scope: TaskScope,
): TaskScope | undefined {
  if (!role.signedIn || role.isAdmin) return undefined;
  return scope;
}

/**
 * The network ids the panel narrows its rows to, given the task scope.
 *
 * `own` rows are the caller's own calls and are MEANT to include networks outside the reader's
 * membership (the platform network): cutting them to the member networks would throw away exactly
 * the rows the toggle exists to show, so the network filter steps aside (`null`). Everything else
 * keeps the Overview's network scope.
 */
export function taskNetworkFilter(
  listScope: TaskScope | undefined,
  ids: Set<string> | null,
): Set<string> | null {
  return listScope === 'own' ? null : ids;
}

export function scopeUsageRows<T extends { tool: string }>(rows: T[], slugs: Set<string> | null): T[] {
  if (!slugs) return rows;
  return rows.filter((r) => {
    const slug = r.tool.split('__')[0];
    return slug.length > 0 && slugs.has(slug);
  });
}
